import { CSSProperties, FC, ReactNode } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export const COLLAPSIBLE_SIDER_TRANSITION =
    'width 0.2s ease, opacity 0.12s ease, border-color 0.2s ease';

type SiderPosition = 'left' | 'right';

interface AnimatedNestedSiderLayoutProps {
    content: ReactNode;
    contentStyle?: CSSProperties;
    open: boolean;
    sider: ReactNode;
    siderLabel?: string;
    siderPosition?: SiderPosition;
    siderStyle?: CSSProperties;
    siderWidth: CSSProperties['width'];
    style?: CSSProperties;
}

/**
 * Keeps nested sider content mounted while its width collapses so resource
 * detail views share the same predictable motion and preserve local state.
 */
const AnimatedNestedSiderLayout: FC<AnimatedNestedSiderLayoutProps> = ({
    content,
    contentStyle,
    open,
    sider,
    siderLabel = 'Resource details',
    siderPosition = 'right',
    siderStyle,
    siderWidth,
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const expandedWidth = typeof siderWidth === 'number' ? `${siderWidth}px` : siderWidth;
    const borderProperty = siderPosition === 'right' ? 'borderLeft' : 'borderRight';

    const contentPanel = (
        <div
            style={{
                flex: '1 1 0%',
                minWidth: 0,
                ...contentStyle,
            }}
        >
            {content}
        </div>
    );
    const siderPanel = (
        <aside
            aria-hidden={!open}
            aria-label={siderLabel}
            style={{
                [borderProperty]: open ? `1px solid ${token.colorBorder}` : '1px solid transparent',
                flex: 'none',
                height: '100%',
                minWidth: 0,
                opacity: open ? 1 : 0,
                overflow: 'hidden',
                pointerEvents: open ? undefined : 'none',
                transition: `${COLLAPSIBLE_SIDER_TRANSITION}, visibility 0s ${open ? '0s' : '0.2s'}`,
                visibility: open ? 'visible' : 'hidden',
                width: open ? expandedWidth : 0,
                ...siderStyle,
            }}
        >
            {sider}
        </aside>
    );

    return (
        <div
            style={{
                display: 'flex',
                minHeight: 0,
                minWidth: 0,
                ...style,
            }}
        >
            {siderPosition === 'left' ? (
                <>
                    {siderPanel}
                    {contentPanel}
                </>
            ) : (
                <>
                    {contentPanel}
                    {siderPanel}
                </>
            )}
        </div>
    );
};

export default AnimatedNestedSiderLayout;
